// src/components/Login.jsx

import React, { useState, useEffect } from 'react';
import useApi from '../../Hooks/useApi';
import useDynamicBackground from './useDynamicBackground';
import QRLoginScanner from './QRLoginScanner';
import translations from './translations.json';
import { FaPlus } from 'react-icons/fa';
import './css/login.css';
import { checkKeychainStatus, unlockKeychain, setupKeychain } from './keychain.js';
import LoginContent from './LoginContent.js';

const Login = () => {
  // --- Basic setup ---
  const preferredLanguage = localStorage.getItem('preferredLanguage') || 'nl';
  const t = translations[preferredLanguage] || translations['nl'];
  const newAccountUrl = 'https://mateza.fillout.com/t/6NnR2Jc6kgus?src=dashboard';

  // --- State hooks ---
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);

  // --- Keychain State ---
  const [showPinLogin, setShowPinLogin] = useState(false);
  const [showKeychainSetup, setShowKeychainSetup] = useState(false);
  const [showPinPrompt, setShowPinPrompt] = useState(false);
  const [passwordTypingStartTime, setPasswordTypingStartTime] = useState(null);

  // --- MFA State ---
  const [showMfaStep, setShowMfaStep] = useState(false);
  const [mfaToken, setMfaToken] = useState('');

  // --- Custom hooks ---
  const dynamicBackgroundUrl = useDynamicBackground();
  const api = useApi();

  // --- Handle magic link token in URL (hash or query) ---
  // Helper function to parse query string (compatible with old browsers)
  const getQueryParam = function(queryString, param) {
    if (!queryString) return null;
    var pairs = queryString.replace(/^\?/, '').split('&');
    for (var i = 0; i < pairs.length; i++) {
      var pair = pairs[i].split('=');
      if (decodeURIComponent(pair[0]) === param) {
        return pair[1] ? decodeURIComponent(pair[1]) : '';
      }
    }
    return null;
  };

  useEffect(() => {
    try {
      var search = window.location.search;
      var hash = window.location.hash;
      var origin = window.location.origin || (window.location.protocol + '//' + window.location.host) || '';
      var pathname = window.location.pathname;

      var token = getQueryParam(search, 'token');
      if (!token && hash) {
        var hashQueryIndex = hash.indexOf('?');
        if (hashQueryIndex !== -1) {
          var hashQuery = hash.substring(hashQueryIndex + 1);
          token = getQueryParam(hashQuery, 'token');
        }
      }

      if (token) {
        localStorage.setItem('accessToken', token);
        localStorage.setItem('loginSuccessful', 'true');

        var cleanedHash = hash ? hash.split('?')[0] : '';
        window.history.replaceState({}, document.title, origin + pathname + cleanedHash);
        window.location.reload();
      }
    } catch (e) {
      console.error('Magic link token parse failed:', e);
    }
  }, []);

  // --- Check for Keychain on component mount ---
  useEffect(() => {
    const checkForKeychain = async () => {
      try {
        const status = await checkKeychainStatus();
        if (status.isInstalled) {
          if (status.hasPin) {
            setShowPinLogin(true);
          } else {
            setShowKeychainSetup(true);
          }
        }
      } catch (err) {
        console.error('Keychain check failed:', err);
      }
    };
    checkForKeychain();
  }, []);

  // --- Handlers ---
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (showPinPrompt || showPinLogin || showKeychainSetup) return;
    if (newPassword.length === 2) setPasswordTypingStartTime(Date.now());
    else if (newPassword.length === 5 && passwordTypingStartTime) {
      if (Date.now() - passwordTypingStartTime > 1000) setShowPinPrompt(true);
      setPasswordTypingStartTime(null);
    } else if (newPassword.length < 2) setPasswordTypingStartTime(null);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const trimmedUser = username.trim();
    if (!trimmedUser) {
      const msg = t.magicLinkUsernameRequired || 'Please enter your username first.';
      setError(msg);
      return;
    }

    try {
      setLoading(true);
      await api.post(window.baseDomain + 'api/auth/request-magic-link', { username: trimmedUser });
      const okMsg = t.magicLinkSent || 'If this username exists, a login link has been sent to your email.';
      setSuccess(okMsg);
    } catch (err) {
      const okMsg = t.magicLinkSent || 'If this username exists, a login link has been sent to your email.';
      setSuccess(okMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleFallbackToPassword = (e) => {
    e.preventDefault();
    setShowPinLogin(false);
    setShowKeychainSetup(false);
    setError('');
  };

  // --- Central Login Logic ---
  const performLogin = async (user, pass) => {
    const trimmedUser = user.trim();
    const trimmedPass = pass.trim();

    // Detect _admin suffix: use as flag only, strip before sending to server
    const isAdminVisit = trimmedUser.endsWith('_admin');
    const loginUser = isAdminVisit ? trimmedUser.slice(0, -6) : trimmedUser;

    setLoading(true);
    setError('');
    setSuccess('');
    setShowMfaStep(false);
    setMfaToken('');

    try {
      const result = await api.post(
        window.baseDomain + 'api/auth/jwt-sign-in/',
        { username: loginUser, password: trimmedPass },
        { withCredentials: true, skipRefresh: true }
      );

      if (result && result.mfaRequired && result.mfaToken) {
        setShowMfaStep(true);
        setMfaToken(result.mfaToken);
        setLoading(false); // Fix: Clear loading state when showing MFA step
        return;
      }

      var accessToken = result && result.accessToken;
      if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('username', loginUser);
        if (isAdminVisit) localStorage.setItem('adminVisit', 'true');
        localStorage.setItem('loginSuccessful', 'true');
        window.location.reload();
      } else {
        throw new Error('Token not received');
      }
    } catch (err) {
      var status = (err && err.response && err.response.status) || (err && err.status) || (err && err.response && err.response.data && err.response.data.status);
      if (status === 429) {
        setError(t.rateLimitError || 'Too many login attempts. Please try again in 5 minutes.');
      } else if (status === 401) {
        setError(t.loginError || 'Incorrect username or password.');
      } else {
        setError(t.loginError || 'Incorrect username or password.');
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Submit Handlers ---
  const handleSubmit = (e) => {
    e.preventDefault();
    performLogin(username, password);
  };

  const handlePinSubmit = async (completedPin) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const credentials = await unlockKeychain(completedPin);
      await performLogin(credentials.username, credentials.password);
    } catch (err) {
      setError(err.message || 'Incorrect PIN.');
      setLoading(false);
    }
  };

  const handleKeychainSetup = async ({ pin, username, password }) => {
    const trimmedUser = username.trim();
    const trimmedPass = password.trim();

    // Detect _admin suffix: flag only, strip before sending to server
    const isAdminVisit = trimmedUser.endsWith('_admin');
    const loginUser = isAdminVisit ? trimmedUser.slice(0, -6) : trimmedUser;

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      // Store raw credentials in keychain (with _admin) so future PIN logins re-trigger the flag
      await setupKeychain(pin, trimmedUser, trimmedPass);

      const result = await api.post(
        window.baseDomain + 'api/auth/jwt-sign-in/',
        { username: loginUser, password: trimmedPass },
        { withCredentials: true, skipRefresh: true }
      );

      if (result && result.mfaRequired && result.mfaToken) {
        setShowMfaStep(true);
        setMfaToken(result.mfaToken);
        setLoading(false);
        return;
      }

      var accessToken = result && result.accessToken;
      if (!accessToken) throw new Error('Login validation failed.');

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('username', loginUser);
      if (isAdminVisit) localStorage.setItem('adminVisit', 'true');
      localStorage.setItem('loginSuccessful', 'true');
      window.location.reload();
    } catch (err) {
      var status = (err && err.response && err.response.status) || (err && err.status) || (err && err.response && err.response.data && err.response.data.status);
      if (status === 429) {
        setError(t.rateLimitError || 'Too many login attempts. Please try again in 5 minutes.');
      } else if (status === 401) {
        setError(t.loginError || 'Incorrect username or password.');
      } else {
        setError(err.message || 'Could not set up keychain. Please check credentials.');
      }
      setLoading(false);
    }
  };

  const handleCancelSetup = () => {
    setShowKeychainSetup(false);
    setError('');
    setSuccess('');
  };

  // --- MFA handlers ---
  const handleMfaVerify = async (code) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const result = await api.post(
        window.baseDomain + 'api/auth/jwt-mfa-verify/',
        { mfaToken, code },
        { withCredentials: true, skipRefresh: true }
      );

      // Strip _admin suffix: flag only, not part of real username
      const trimmedUser = username.trim();
      const isAdminVisit = trimmedUser.endsWith('_admin');
      const loginUser = isAdminVisit ? trimmedUser.slice(0, -6) : trimmedUser;

      var accessToken = result && result.accessToken;
      if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('username', loginUser);
        if (isAdminVisit) localStorage.setItem('adminVisit', 'true');
        localStorage.setItem('loginSuccessful', 'true');
        window.location.reload();
        return;
      }
      throw new Error('Token not received');
    } catch (err) {
      var status = (err && err.response && err.response.status) || (err && err.status) || (err && err.response && err.response.data && err.response.data.status);
      if (status === 429) {
        setError(t.rateLimitError || 'Too many login attempts. Please try again in 5 minutes.');
      } else if (status === 401) {
        setError(t.mfaInvalid || 'Invalid code. Please try again.');
      } else {
        setError(t.mfaInvalid || 'Invalid code. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleMfaBack = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setShowMfaStep(false);
    setMfaToken('');
    setError('');
  };

  return (
    <div className="login-page">
      <div
        className="login-container"
        style={{
          backgroundImage: `url(${dynamicBackgroundUrl || 'https://static.reservaties.net/backgrounds/blue.png'})`,
        }}
      >
        <a
          href={newAccountUrl}
          className="afspraak-button"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaPlus size={16} /> {t.newAccount}
        </a>

        <LoginContent
          t={t}
          newAccountUrl={newAccountUrl}
          username={username}
          setUsername={setUsername}
          password={password}
          handlePasswordChange={handlePasswordChange}
          rememberMe={rememberMe}
          setRememberMe={setRememberMe}
          loading={loading}
          error={error}
          success={success}
          showPinPrompt={showPinPrompt}
          setShowPinPrompt={setShowPinPrompt}
          showMfaStep={showMfaStep}
          showKeychainSetup={showKeychainSetup}
          showPinLogin={showPinLogin}
          onForgotPassword={handleForgotPassword}
          onSubmit={handleSubmit}
          onPinSubmit={handlePinSubmit}
          onFallbackToPassword={handleFallbackToPassword}
          onKeychainSetup={handleKeychainSetup}
          onCancelSetup={handleCancelSetup}
          onMfaVerify={handleMfaVerify}
          onMfaBack={handleMfaBack}
          onOpenQRScan={() => setShowQRScanner(true)}
        />
      </div>

      {showQRScanner && <QRLoginScanner onClose={() => setShowQRScanner(false)} />}
    </div>
  );
};

export default Login;
